import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * The read loop behind every server-paged Super Admin list: `GET path?limit&skip&<filters>`
 * answering `{ total, items, ... }`.
 *
 * The caller owns `page` / `setPage` (its filter handlers reset the page in the same update
 * as the filter), the hook owns everything about the read:
 *
 * - The list on screen is a function of [filters, page, request] and nothing else. A reload
 *   is a state bump, so it always reads the filters and page the operator is on NOW, even when
 *   it is asked for after an awaited request. The effect cleanup is the request ticket: only
 *   the newest request may land.
 * - `filters` is a flat object; blank values (`""`, null, undefined) are not sent.
 * - A 200 that is not the page (the production proxy answers 200 + index.html for an unknown
 *   /api path) is a failed read, not an empty list.
 * - A page that emptied under the operator (its last row left) steps back. The target is
 *   always strictly below the current page: stepping to "the last page that exists" can name
 *   the page already shown, and setting state to its current value re-runs nothing, which
 *   used to leave the list busy forever.
 * - `reload()` re-reads the current page. It ignores its arguments, so it is safe as an
 *   `onClick` / `onSaved` handler. `retry` is the same function, named for the failure panel.
 * - `loadError` is what the newest read failed with (`null` after a success), so a screen can tell
 *   a refusal (403, see NoAccess.jsx) from an outage.
 *
 * `enabled: false` sends nothing (the caller knows the filters cannot be valid, e.g. an inverted
 * date range); the last good `data` stays.
 *
 * Returns `{ data, totalForCurrent, loading, loadFailed, loadError, reload, retry }`. `data` is
 * the last good response, kept on screen while a newer read is pending or after one failed.
 * `totalForCurrent` is the count a caller may print: `data.total` only when that response
 * answered exactly the filters now in force and the newest read did not fail; `null` before the
 * first answer, while new filters are loading, while disabled, and after a failure. A page turn
 * keeps it (the total belongs to the filters, not the page).
 */
const blank = (v) => v === "" || v === null || v === undefined;

/**
 * The filters a read sends, as a stable string key: blank values dropped. Two filter
 * objects that send the same request have the same key, so a read is keyed by what it
 * asked, not by object identity. `JSON.parse(key)` is the request's params.
 */
export const filterParamsKey = (filters) =>
  JSON.stringify(Object.fromEntries(Object.entries(filters).filter(([, v]) => !blank(v))));

export function usePagedList({ path, filters, page, setPage, pageSize, enabled = true }) {
  // The last good response and the filter key it answered.
  const [landed, setLanded] = useState(null); // { key, data } | null
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Bumped by reload(); a new value re-runs the read with the filters and page in force now.
  const [request, setRequest] = useState(0);

  const filterKey = filterParamsKey(filters);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const params = { limit: pageSize, skip: (page - 1) * pageSize, ...JSON.parse(filterKey) };
    setLoading(true);
    api.get(path, { params })
      .then((r) => {
        if (!r.data || !Array.isArray(r.data.items) || !Number.isFinite(r.data.total)) {
          throw new Error(`${path} payload`);
        }
        if (cancelled) return;
        if (r.data.items.length === 0 && r.data.total > 0 && page > 1) {
          setPage(Math.max(1, Math.min(Math.ceil(r.data.total / pageSize), page - 1)));
          return;
        }
        setLanded({ key: filterKey, data: r.data });
        setLoadError(null);
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadError(error);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [enabled, path, filterKey, page, pageSize, request, setPage]);

  const reload = useCallback(() => setRequest((n) => n + 1), []);
  const loadFailed = loadError !== null;

  const data = landed ? landed.data : null;
  const totalForCurrent = landed && landed.key === filterKey && !loadFailed ? landed.data.total : null;

  return { data, totalForCurrent, loading, loadFailed, loadError, reload, retry: reload };
}
